import { Download, Pause, Play, RotateCcw, X } from 'lucide-react';
import { useGlobalMusic } from '../../context/GlobalMusicContext';
import { useI18n } from '../../context/LanguageContext';

const formatAudioTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

interface PersistentMusicBarProps {
  onClose?: () => void;
}

export default function PersistentMusicBar({ onClose }: PersistentMusicBarProps) {
  const { t } = useI18n();
  const {
    currentTrack,
    hasTrack,
    isPlaying,
    currentTime,
    duration,
    progress,
    toggleTrack,
    restartTrack,
    seekPercent,
  } = useGlobalMusic();

  if (!hasTrack || !currentTrack) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[80] border-t border-skoleom-lime/35 bg-black/92 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1500px] flex-col gap-2 px-4 py-2 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold uppercase tracking-[0.14em] text-skoleom-lime">
              {t('home.nowPlaying')}
            </p>
            <p className="truncate text-sm font-bold text-white">{currentTrack.title}</p>
            <p className="truncate text-[11px] text-white/60">{currentTrack.artist}</p>
          </div>

          <button
            type="button"
            onClick={() => toggleTrack(currentTrack)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-skoleom-lime text-black"
            aria-label={isPlaying ? t('common.pause') : t('common.play')}
          >
            {isPlaying ? <Pause size={15} /> : <Play size={15} fill="currentColor" />}
          </button>

          <button
            type="button"
            onClick={() => restartTrack(currentTrack)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 text-white"
            aria-label={t('home.restartTrack')}
          >
            <RotateCcw size={15} />
          </button>

          <a
            href={currentTrack.downloadUrl || currentTrack.src}
            download
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-skoleom-lime/45 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white"
          >
            <Download size={14} />
            {t('home.downloadTrack')}
          </a>

          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 text-white"
              aria-label={t('common.hide')}
              title={t('common.hide')}
            >
              <X size={15} />
            </button>
          ) : null}
        </div>

        <div className="flex items-center gap-2 text-[11px] text-white/65">
          <span>{formatAudioTime(currentTime)}</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.max(0, Math.min(100, Math.round(progress * 100)))}
            onChange={(event) => seekPercent(Number(event.target.value))}
            className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/15 accent-skoleom-lime"
            aria-label={t('home.seekTrack')}
          />
          <span>{formatAudioTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}
